import type { CSSProperties, ReactNode } from "react";

// ─── Atoms ────────────────────────────────────────────────────────────────

export function Eyebrow({
  children,
  style,
  className,
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={"t-caps " + (className || "")}
      style={{ marginBottom: 12, ...style }}
    >
      {children}
    </div>
  );
}

export function HeadingItalic({
  children,
  size = 30,
  style,
  as: As = "h1",
}: {
  children: ReactNode;
  size?: number;
  style?: CSSProperties;
  as?: keyof JSX.IntrinsicElements;
}) {
  return (
    <As
      style={{
        fontFamily: "var(--f-display)",
        fontWeight: 400,
        fontSize: size,
        lineHeight: 1.08,
        letterSpacing: "-0.015em",
        margin: "0 0 14px",
        color: "var(--ink)",
        ...style,
      }}
    >
      {children}
    </As>
  );
}

export function HelperText({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <p
      style={{
        fontFamily: "var(--f-body)",
        fontSize: 16,
        lineHeight: 1.45,
        color: "var(--ink-soft)",
        margin: "0 0 24px",
        ...style,
      }}
    >
      {children}
    </p>
  );
}

export function Swoosh({ children }: { children: ReactNode }) {
  return <span className="swoosh">{children}</span>;
}

export function ErrorText({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        fontFamily: "var(--f-body)",
        fontStyle: "italic",
        fontSize: 13,
        lineHeight: 1.4,
        color: "var(--error)",
        ...style,
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ marginTop: 2, flexShrink: 0 }}
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <span>{children}</span>
    </div>
  );
}

export function Fleuron({ style }: { style?: CSSProperties }) {
  return (
    <div className="fleuron" style={{ fontSize: 14, ...style }} aria-hidden>
      ❦
    </div>
  );
}

export function TapeCorner({
  rotation = -2,
  width = 38,
  height = 14,
  style,
}: {
  rotation?: number;
  width?: number;
  height?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        width,
        height,
        background: "rgba(168,122,58,0.35)",
        boxShadow: "0 1px 2px rgba(43,26,15,0.10)",
        transform: `rotate(${rotation}deg)`,
        ...style,
      }}
    />
  );
}

export function PaperCard({
  children,
  style,
  className,
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        background: "var(--paper-50)",
        borderRadius: 14,
        padding: "18px",
        border: "1px solid rgba(43,26,15,0.10)",
        boxShadow:
          "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
        position: "relative",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Landing helpers ──────────────────────────────────────────────────────

export function CharacterCard({
  who,
  name,
  desc,
}: {
  who: "konrad" | "ida";
  name: string;
  desc: string;
}) {
  const src = who === "ida" ? "/images/ida.jpg" : "/images/konrad.jpg";
  return (
    <div
      style={{
        background: "var(--paper-50)",
        borderRadius: 14,
        padding: "14px 12px 12px",
        border: "1px solid rgba(43,26,15,0.10)",
        boxShadow: "0 4px 12px rgba(43,26,15,0.06)",
        position: "relative",
        transform: who === "ida" ? "rotate(0.6deg)" : "rotate(-0.8deg)",
      }}
    >
      <TapeCorner
        rotation={-2}
        style={{
          top: -8,
          left: "50%",
          transform: "translateX(-50%) rotate(-2deg)",
        }}
      />
      <div
        className="oval-frame"
        style={{
          width: "100%",
          aspectRatio: "1 / 1.05",
          background: "var(--paper-100)",
          overflow: "hidden",
        }}
      >
        <img
          src={src}
          alt={name}
          className="photo-tone"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <div
          className="t-script"
          style={{ fontSize: 30, lineHeight: 1, color: "var(--oxblood)" }}
        >
          {name}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
            marginTop: 2,
          }}
        >
          {desc}
        </div>
      </div>
    </div>
  );
}

export function StepRow({
  n,
  title,
  desc,
}: {
  n: string;
  title: string;
  desc: string;
}) {
  return (
    <div className="step-row" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div
        className="step-num"
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 44,
          lineHeight: 0.9,
          color: "var(--oxblood)",
          flexShrink: 0,
          width: 56,
          paddingTop: 2,
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1, paddingTop: 6 }}>
        <h3
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 21,
            margin: "0 0 4px",
            color: "var(--ink)",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 16,
            lineHeight: 1.45,
            color: "var(--ink-soft)",
            margin: 0,
          }}
        >
          {desc}
        </p>
      </div>
    </div>
  );
}

export function TestimonialCard({
  text,
  who,
  rel,
  rot,
  photo,
  photoAlt,
}: {
  text: string;
  who: string;
  rel: string;
  rot: number;
  photo?: string;
  photoAlt?: string;
}) {
  return (
    <div
      style={{
        flex: "0 0 78%",
        scrollSnapAlign: "start",
        background: "var(--paper-50)",
        borderRadius: 3,
        padding: photo ? "0 0 16px" : "20px 18px 16px",
        border: "1px solid rgba(43,26,15,0.10)",
        boxShadow: "0 10px 20px -6px rgba(43,26,15,0.16)",
        transform: `rotate(${rot}deg)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <TapeCorner
        width={50}
        height={14}
        rotation={-3}
        style={{
          top: -7,
          left: 18,
          background: "rgba(168,122,58,0.30)",
          zIndex: 2,
        }}
      />
      {photo && (
        <img
          src={photo}
          alt={photoAlt || ""}
          className="photo-tone"
          style={{
            display: "block",
            width: "100%",
            height: 160,
            objectFit: "cover",
            marginBottom: 14,
          }}
        />
      )}
      <div style={{ padding: photo ? "0 18px" : 0 }}>
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 38,
          lineHeight: 0.6,
          color: "var(--oxblood)",
          marginBottom: 6,
        }}
      >
        „
      </div>
      <p
        style={{
          fontFamily: "var(--f-body)",
          fontSize: 15.5,
          lineHeight: 1.45,
          color: "var(--ink)",
          margin: "0 0 18px",
        }}
      >
        {text}
      </p>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div
          className="t-script"
          style={{ fontSize: 22, color: "var(--oxblood)" }}
        >
          {who}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
          }}
        >
          · {rel}
        </div>
      </div>
      </div>
    </div>
  );
}

export function TrustTile({
  icon,
  title,
  desc,
}: {
  icon: "lock" | "shield" | "phone" | "pause";
  title: string;
  desc: string;
}) {
  return (
    <div
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 12,
        padding: "14px 14px",
      }}
    >
      <TrustIcon name={icon} />
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 500,
          fontSize: 15.5,
          color: "var(--ink)",
          marginTop: 8,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: "var(--f-body)",
          fontSize: 13,
          lineHeight: 1.4,
          color: "var(--ink-soft)",
          marginTop: 3,
        }}
      >
        {desc}
      </div>
    </div>
  );
}

function TrustIcon({ name }: { name: "lock" | "shield" | "phone" | "pause" }) {
  const s = {
    width: 22,
    height: 22,
    fill: "none" as const,
    stroke: "var(--oxblood)",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (name === "lock")
    return (
      <svg viewBox="0 0 24 24" {...s} aria-hidden>
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 018 0v3" />
      </svg>
    );
  if (name === "shield")
    return (
      <svg viewBox="0 0 24 24" {...s} aria-hidden>
        <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  if (name === "phone")
    return (
      <svg viewBox="0 0 24 24" {...s} aria-hidden>
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" {...s} aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

// ─── Shared icons ─────────────────────────────────────────────────────────

export function ArrowRightIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

export function PhoneIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

export function Wordmark({
  size = 22,
  color = "var(--ink)",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <div
      style={{
        fontFamily: "var(--f-display)",
        fontSize: size,
        fontStyle: "italic",
        color,
        letterSpacing: "-0.01em",
        lineHeight: 1,
      }}
    >
      Vitanara
      <span
        style={{
          display: "inline-block",
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "var(--oxblood)",
          marginLeft: 4,
          verticalAlign: "middle",
        }}
      />
    </div>
  );
}
